import React, { useState } from "react"
import { Search, Smartphone, FileBarChart, Activity, Share2 } from "lucide-react"
import ReportAppSearchModal from "./ReportAppSearchModal"
import AICMOSummaryTab from "./tabs/AICMOSummaryTab"
import AIReportSummaryTab from "./tabs/AIReportSummaryTab"
import AIForecastingSummaryTab from "./tabs/AIForecastingSummaryTab"
import SocialComparisonSummaryTab from "./tabs/SocialComparisonSummaryTab"
import { REPORT_COLORS, REPORT_RADIUS } from "./components/reportTheme"
import SEO from "../../components/Common/SEO"

const TABS = [
  { key: "ai-cmo", label: "AI CMO", icon: Smartphone },
  { key: "ai-report", label: "AI Reports", icon: FileBarChart },
  { key: "forecasting", label: "AI Forecasting", icon: Activity },
  { key: "social", label: "Social Comparison", icon: Share2 },
]

export default function Report() {
  const [selectedApp, setSelectedApp] = useState(null)
  const [searchOpen, setSearchOpen] = useState(true)
  const [activeTab, setActiveTab] = useState(TABS[0].key)

  const handleAppSelect = app => {
    const itemId = app?.item_id || app?._id
    if (!itemId) return
    setSelectedApp({
      item_id: itemId,
      name: app.name || "",
      image_url: app.image_url || "",
      platform: app.platform || "",
      category: app.category || "",
    })
    setActiveTab(TABS[0].key)
    setSearchOpen(false)
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="container-fluid" style={{ padding: "32px 16px" }}>
        <SEO title="App Report | KeyQuest" description="Combined AI CMO, AI Reports, AI Forecasting and Social Comparison view for a single app." robots="noindex, nofollow" />

        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: 16 }}>
          <div>
            <h1 style={{ color: REPORT_COLORS.textPrimary, fontSize: 28, fontWeight: 700, margin: 0 }}>App Report</h1>
            <p style={{ color: REPORT_COLORS.textSecondary, fontSize: 14, margin: "4px 0 0" }}>
              A condensed AI CMO, AI Reports, AI Forecasting and Social Comparison summary for one app.
            </p>
          </div>
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              background: REPORT_COLORS.accent,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <Search size={16} /> {selectedApp ? "Change App" : "Search App"}
          </button>
        </div>

        {selectedApp && (
          <div className="d-flex align-items-center mb-4" style={{ gap: 10, color: REPORT_COLORS.textPrimary }}>
            <img
              src={selectedApp.image_url || "https://via.placeholder.com/40"}
              alt={selectedApp.name}
              style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }}
            />
            <span style={{ fontWeight: 600, fontSize: 16 }}>{selectedApp.name}</span>
          </div>
        )}

        {!selectedApp ? (
          <div style={{ color: REPORT_COLORS.textMuted, textAlign: "center", padding: "80px 0" }}>
            Search for an app to generate its combined report.
          </div>
        ) : (
          <React.Fragment key={selectedApp.item_id}>
            <div
              style={{
                display: "flex",
                width: "100%",
                backgroundColor: REPORT_COLORS.cardBg,
                border: `1px solid ${REPORT_COLORS.cardBorder}`,
                borderRadius: REPORT_RADIUS,
                padding: 5,
                gap: 4,
                marginBottom: 24,
              }}
            >
              {TABS.map(tab => {
                const isActive = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "12px 16px",
                      borderRadius: REPORT_RADIUS - 5,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      transition: "background-color 0.2s ease, color 0.2s ease",
                      backgroundColor: isActive ? REPORT_COLORS.accent : "transparent",
                      color: isActive ? "#fff" : REPORT_COLORS.textMuted,
                    }}
                    onMouseEnter={e => {
                      if (!isActive) e.currentTarget.style.backgroundColor = REPORT_COLORS.cardBgRaised
                    }}
                    onMouseLeave={e => {
                      if (!isActive) e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <tab.icon size={16} />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>

            {/* All four tabs mount together so their APIs fire immediately; only the active one is shown */}
            <div style={{ display: activeTab === "ai-cmo" ? "block" : "none" }}>
              <AICMOSummaryTab appId={selectedApp.item_id} appName={selectedApp.name} />
            </div>

            <div style={{ display: activeTab === "ai-report" ? "block" : "none" }}>
              <AIReportSummaryTab appId={selectedApp.item_id} appName={selectedApp.name} />
            </div>

            <div style={{ display: activeTab === "forecasting" ? "block" : "none" }}>
              <AIForecastingSummaryTab appId={selectedApp.item_id} appName={selectedApp.name} />
            </div>

            <div style={{ display: activeTab === "social" ? "block" : "none" }}>
              <SocialComparisonSummaryTab appId={selectedApp.item_id} appName={selectedApp.name} />
            </div>
          </React.Fragment>
        )}

        <ReportAppSearchModal isOpen={searchOpen} toggle={() => setSearchOpen(false)} onAppSelect={handleAppSelect} />
      </div>
    </div>
  )
}
